import React, {useCallback, useContext, useEffect, useState} from 'react';
import ReactDOM from 'react-dom';
import {sdk} from '@farcaster/miniapp-sdk';
import {useLocation} from 'react-router-dom';
import bs58 from 'bs58';
import {USER_SESSION_EXPIRED_EVENT} from '../../utils/authErrors';
import {getAccountRoleFromUserData, getQaRoleFromUserData} from '../../utils/userRoles';

import styles from './ConnectWalletHeader.module.css';
import email_svg from '../../static_files/connect-email.svg';

import okx_icon from '../../static_files/OKX Wallet.png';
import walletconnect_icon from '../../static_files/walletconnect-icon.svg';
import Phantom_icon from '../../static_files/Phantom Wallet.svg';
import metamask_icon from '../../static_files/MetaMask-icon.svg';

import solana_chain_icon from '../../static_files/solana-chain.svg';
import eth_chain_icon from '../../static_files/eth-chain.svg';
import base_chain_icon_svg from '../../static_files/Base_square_blue.svg';
import monad_chain_icon from '../../static_files/monad-chain.svg';

import okx_icon_mobile from '../../static_files/OKX Wallet.png';
import walletconnect_icon_mobile from '../../static_files/walletconnect-icon.png';
import Phantom_icon_mobile from '../../static_files/Phantom Wallet.png';
import metamask_icon_mobile from '../../static_files/MetaMask-icon-fox.png';

import menu_icon from "../../static_files/menu.svg";
import logo_svg from "../../static_files/PrismaxLogoWhite.svg";
import rejectImg from '../../static_files/rejectImg.svg';
import cancle_icon from '../../static_files/cancel.svg';
import successImg from '../../static_files/successImg.svg';
import warningImg from '../../static_files/warningImg.svg';
import disconnectImg from '../../static_files/disconnect.svg';
import emailLogoutImg from "../../static_files/emailLogout.svg";
import menuDropDownImg from '../../static_files/MenuDropDown.svg'
import {
    chains,
    fetchBalancesAndNotifyParent,
    fetchEVMBalancesAndNotifyParent,
    fetchEVMTokenBalances,
    fetchThreeTokenBalancesWithoutProvider,
    fetchTokenBalances,
    getTokenAccountFromWalletAddress,
    metadata,
    projectId,
    isEvmChain,
    EVM_CHAIN_CONFIG,
    getEvmChainConfig,
    getNativeCurrencyDisplayName,
    getExplorerBaseUrl,
} from './ConnectWalletHeaderOnchainHelper';
import {Connection, PublicKey, Transaction, TransactionInstruction} from '@solana/web3.js';

// Email login
import {useAuth} from '../../context/AuthContext';

// wallet connect
import {
    createWeb3Modal,
    defaultSolanaConfig,
    useWeb3Modal,
    useWeb3ModalAccount,
    useWeb3ModalProvider
} from '@web3modal/solana/react';
import {solana} from '@web3modal/solana/chains';


import {createTransferInstruction, getOrCreateAssociatedTokenAccount,} from '@solana/spl-token';
import SideMenu from "./MobileMenu";
import {SwapMainAppContext} from "../../context/SwapMainAppContext";
import {APP_URL, PRISMAX_BACKEND_URL, RPC_URL} from "../../configs/config";
import {useRecaptcha} from "../../utils/useRecaptcha";
import {isTestMode} from "../../config/testMode";
import ConnectModal from './ConnectModal';

/**
 * Props:
 *  - onWalletConnected : pass out Component， let parent know the address + balance
 *  - amount: user input mint/redeem amount
 *  - transactionTrigger: transaction triggered?
 *  - selectedToken: what transaction token is being used
 */

let web3ModalInitialized = false;

const CHAIN_ICON_MAP = {
    solana: solana_chain_icon,
    ethereum: eth_chain_icon,
    base: base_chain_icon_svg,
    monad: monad_chain_icon
};

const CHAIN_OPTIONS = [
    {key: 'solana', label: 'Solana', icon: solana_chain_icon},
    ...Object.values(EVM_CHAIN_CONFIG).map(({key, label}) => ({
        key,
        label,
        icon: CHAIN_ICON_MAP[key] || solana_chain_icon
    }))
];

const CONNECT_DEFAULT_TEXT = 'Connect';
const WALLET_STANDARD_SOLANA_MAINNET = 'solana:mainnet';

const initializeWeb3ModalIfNeeded = () => {
    if (web3ModalInitialized) return;

    const isTestEnv = (typeof window !== 'undefined' && window.Cypress) ||
        (typeof window !== 'undefined' && window.localStorage?.getItem('TEST_MODE') === 'true') ||
        process.env.REACT_APP_TEST_MODE === 'true';

    if (isTestEnv) {
        console.log('🧪 Web3Modal initialization completely skipped in test mode');
        web3ModalInitialized = true;
        return;
    }

    try {
        createWeb3Modal({
            solanaConfig: defaultSolanaConfig({
                metadata,
                defaultChain: solana,
                enableInjected: true
            }), // required
            chains, // required
            projectId,
            enableAnalytics: true,
        });

        web3ModalInitialized = true;
    } catch (error) {
        console.error('Web3Modal initialization failed:', error);
    }
};

initializeWeb3ModalIfNeeded();

const getWalletStandardWallets = async () => {
    if (typeof window === 'undefined') return [];
    const wallets = new Set();
    const api = {
        register: (...walletsToRegister) => {
            walletsToRegister.filter(Boolean).forEach((wallet) => wallets.add(wallet));
        }
    };

    const handleRegister = (event) => {
        try {
            const callback = event?.detail;
            if (typeof callback === 'function') {
                callback(api);
            }
        } catch (error) {
            console.error('wallet-standard registration error:', error);
        }
    };

    window.addEventListener('wallet-standard:register-wallet', handleRegister);
    try {
        window.dispatchEvent(new CustomEvent('wallet-standard:app-ready', {detail: api}));
    } catch (error) {
        console.error('wallet-standard app-ready dispatch failed:', error);
    }
    await new Promise((resolve) => setTimeout(resolve, 0));
    window.removeEventListener('wallet-standard:register-wallet', handleRegister);

    return Array.from(wallets);
};

const findMetaMaskWalletStandard = async () => {
    const wallets = await getWalletStandardWallets();
    return wallets.find((wallet) => wallet?.name?.toLowerCase().includes('metamask'));
};

const getWalletStandardSolanaAccount = (wallet, accounts) => {
    const availableAccounts = accounts?.length ? accounts : wallet?.accounts || [];
    const mainnetAccount = availableAccounts.find((account) =>
        account?.chains?.includes(WALLET_STANDARD_SOLANA_MAINNET)
    );
    if (mainnetAccount) return mainnetAccount;
    return availableAccounts.find((account) =>
        account?.chains?.some((chain) => chain?.startsWith('solana:'))
    );
};

const getWalletStandardSolanaChain = (wallet, account) => {
    const walletChains = wallet?.chains || [];
    const accountChains = account?.chains || [];
    if (walletChains.includes(WALLET_STANDARD_SOLANA_MAINNET) &&
        accountChains.includes(WALLET_STANDARD_SOLANA_MAINNET)) {
        return WALLET_STANDARD_SOLANA_MAINNET;
    }
    const accountChain = accountChains.find((chain) => chain?.startsWith('solana:'));
    if (accountChain) return accountChain;
    const walletChain = walletChains.find((chain) => chain?.startsWith('solana:'));
    return walletChain || WALLET_STANDARD_SOLANA_MAINNET;
};

const createWalletStandardSolanaProvider = (wallet, account, chain) => {
    const signTransactionFeature = wallet?.features?.['solana:signTransaction'];
    const signAndSendFeature = wallet?.features?.['solana:signAndSendTransaction'];
    const signMessageFeature = wallet?.features?.['solana:signMessage'];
    const accountPublicKey = account?.publicKey || (account?.address ? bs58.decode(account.address) : null);

    const serializeTransaction = (transaction) =>
        transaction.serialize({requireAllSignatures: false, verifySignatures: false});

    return {
        publicKey: accountPublicKey ? new PublicKey(accountPublicKey) : null,
        signTransaction: async (transaction) => {
            if (!signTransactionFeature?.signTransaction) {
                throw new Error('MetaMask does not support solana:signTransaction.');
            }
            const [output] = await signTransactionFeature.signTransaction({
                account,
                transaction: serializeTransaction(transaction),
                chain
            });
            return Transaction.from(output.signedTransaction);
        },
        signAndSendTransaction: async (transaction) => {
            const serialized = serializeTransaction(transaction);
            if (signAndSendFeature?.signAndSendTransaction) {
                const [output] = await signAndSendFeature.signAndSendTransaction({
                    account,
                    transaction: serialized,
                    chain
                });
                return {signature: bs58.encode(output.signature)};
            }
            if (!signTransactionFeature?.signTransaction) {
                throw new Error('MetaMask does not support solana:signAndSendTransaction.');
            }
            const [output] = await signTransactionFeature.signTransaction({
                account,
                transaction: serialized,
                chain
            });
            const signedTransaction = Transaction.from(output.signedTransaction);
            const connection = new Connection(RPC_URL, 'confirmed');
            const signature = await connection.sendRawTransaction(signedTransaction.serialize());
            return {signature};
        },
        signMessage: async (message) => {
            if (!signMessageFeature?.signMessage) {
                throw new Error('MetaMask does not support solana:signMessage.');
            }
            const [output] = await signMessageFeature.signMessage({account, message});
            return {signature: output.signature};
        },
        disconnect: async () => {}
    };
};

const ConnectWalletHeader = ({
                                 onWalletConnected,
                                 showPanel,
                                 setShowPanel,
                                 requireSignature = false, // signin for admin page
                                 adminWhitelist = [],       // for admin page,
                                 setAdminLogin,
                                 setAdminAccessToken
                             }) => {
    const location = useLocation();
    const {
        notification, showNotification,
        usdtTokenAddress,
        usdcTokenAddress,
        prismaxRecevingAddress,
        prismaxEvmReceivingAddress,
        transactionTrigger,
        connectWalletModalRequest,
        connectWalletModalForceWalletList,
        requestConnectWalletModal,
        selectedToken,
        amount,
        onMembershipPaymentSuccess,
        userEmail,
        setUserEmail,
        setUserClass,
        setUserRole,
        setQaRole,
        setIsProcessing,
        setUsdcBalance,
        setUsdtBalance,
        setGatewayToken,
        clearLocalSessionData,
        setUserId,
        selectedChain,
        setSelectedChain,
        setLinkedWalletChain,
        fetchUserInfoAfterConnect: contextFetchUserInfo,
        referralCode,
        setReferralCode
    } = useContext(SwapMainAppContext);
    const {
        walletAddress: connectedPublicKey,
        // rename setWalletAddress to setConnectedPublicKey locally
        //    connectedPublicKey =  complete address
        setWalletAddress: setConnectedPublicKey,
    } = useContext(SwapMainAppContext);
    // Track which wallet is connected
    const [connectedWalletType, setConnectedWalletType] = useState(null);
    // Shorten version address
    const [walletAddress, setWalletAddress] = useState(() => {
        const storedAddress = connectedPublicKey;
        return storedAddress ? shortenAddress(storedAddress) : CONNECT_DEFAULT_TEXT;
    });

    const [isSideMenuOpen, setIsSideMenuOpen] = useState(false);
    const [selectedMenuItem, setSelectedMenuItem] = useState('myAssets');
    const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);
    const [openWalletListOnConnectModalOpen, setOpenWalletListOnConnectModalOpen] = useState(false);
    const [forceWalletConnectModalView, setForceWalletConnectModalView] = useState(false);


    const [okxSolanaProvider, setOkxSolanaProvider] = useState(null);
    const [coinbaseSolanaProvider, setCoinbaseSolanaProvider] = useState(null);
    const [phantomSolanaProvider, setPhantomSolanaProvider] = useState(null);
    const [metamaskSolanaProvider, setMetamaskSolanaProvider] = useState(null);

    // For wallet Connect
    const [walletConnectProvider, setWalletConnectProvider] = useState(null);
    const [isBaseMiniApp, setIsBaseMiniApp] = useState(false);
    const [baseAutoConnectAttempted, setBaseAutoConnectAttempted] = useState(false);

    // Web3Modal hooks -testing mode
    const useWeb3ModalSafe = () => {
        const testMode = isTestMode();

        // Always call hooks at the top level, not inside try-catch
        const web3Modal = useWeb3Modal();
        const account = useWeb3ModalAccount();
        const provider = useWeb3ModalProvider();

        return {
            web3Modal,
            account,
            provider,
            testMode
        };
    };

    const {web3Modal, account, provider, testMode} = useWeb3ModalSafe();

    let open, modalAddress, walletProvider;

    if (testMode) {
        console.log('🧪 Using mock Web3Modal functions in test mode');
        open = () => console.log('🧪 Web3Modal open mocked');
        modalAddress = null;
        walletProvider = null;
    } else {
        open = web3Modal?.open || (() => console.log('🧪 Web3Modal open not available'));
        modalAddress = account?.address || null;
        walletProvider = provider?.walletProvider || null;
    }

    // For detecting if the wallet extension has been installed
    const [isOkxDetected, setIsOkxDetected] = useState(false);
    const [isCoinbaseDetected, setIsCoinbaseDetected] = useState(false);
    const [isPhantomDetected, setIsPhantomDetected] = useState(false);
    const [isMetaMaskDetected, setIsMetaMaskDetected] = useState(false);

    // Track if auto-reconnection has been attempted to prevent multiple attempts
    const [autoReconnectionAttempted, setAutoReconnectionAttempted] = useState(false);

    //Gat way:
    const {logout, gatewayUserData} = useAuth();
    const {executeRecaptchaAction, isRecaptchaReady} = useRecaptcha();

    const requestSignature = async (provider, publicKey) => {
        const message = "Sign this message to authenticate as admin for PrismaX AI Admin Portal.";
        const encodedMessage = new TextEncoder().encode(message);

        let signature;

        // Different chain support for admin signature
        const chainSpecificMessage = selectedChain === 'solana'
            ? message
            : `${message}\n\nChain: ${selectedChain}`;
        const encodedChainMessage = new TextEncoder().encode(chainSpecificMessage);

        // Handle different wallet types
        if (provider.signMessage) {
            const result = await provider.signMessage(encodedMessage);

            // Check if result is an object with signature property (OKX wallet case)
            if (result && typeof result === 'object' && 'signature' in result) {
                signature = result.signature;
            } else {
                signature = result;
            }
        } else if (provider.request) {
            const response = await provider.request({
                method: 'signMessage',
                params: {
                    message: encodedMessage,
                    display: 'utf8'
                }
            });
            signature = response.signature;
        }

        let signatureBase58;
        if (signature instanceof Uint8Array) {
            signatureBase58 = bs58.encode(signature);
        } else if (typeof signature === 'string') {
            signatureBase58 = signature;
        } else {
            console.error('Unexpected signature type:', typeof signature, signature);
            throw new Error('Unexpected signature format');
        }

        return signatureBase58;
    };

    const applyAdminSession = ({access_token: accessToken, refresh_token: refreshToken}) => {
        if (!accessToken || !refreshToken) {
            throw new Error('Admin login did not return a complete session.');
        }
        setAdminAccessToken(accessToken);
        localStorage.setItem('prismax_admin_token', accessToken);
        localStorage.setItem('prismax_admin_refresh_token', refreshToken);
        setAdminLogin(true);
    };
    // wallet Connect Use effect
    useEffect(() => {
        if (modalAddress) {
            setWalletAddress(shortenAddress(modalAddress));
            setConnectedPublicKey(modalAddress);
        }
    }, [modalAddress]);

    useEffect(() => {
        if (walletProvider) {
            if (connectedWalletType !== null) {
                handleWalletConnectSolana();
            }
        }
    }, [walletProvider]);

    // address from email user
    useEffect(() => {
        if ((localStorage.getItem("userEmail") && localStorage.getItem("emailUserWalletAddress") !== "null" && localStorage.getItem("emailUserWalletAddress") !== null && localStorage.getItem("emailUserWalletAddress") !== "requested")) {
            setWalletAddress(shortenAddress(localStorage.getItem("emailUserWalletAddress")));

        } else if (localStorage.getItem("userEmail") && gatewayUserData) {
            setWalletAddress(CONNECT_DEFAULT_TEXT);
        }
    }, [gatewayUserData]);

    // -----------------------------------------
    // Use Effect for handling transactions
    // -----------------------------------------
    // Handle Transfer trigger ->
    useEffect(() => {
        const run = async () => {
            try {
                if (selectedChain === 'solana') {
                    let RECEIVING_TOKEN_ACCOUNT;
                    if (selectedToken === 'USDC') {
                        RECEIVING_TOKEN_ACCOUNT = await getTokenAccountFromWalletAddress(
                            prismaxRecevingAddress,
                            usdcTokenAddress
                        );
                    } else if (selectedToken === 'USDT') {
                        RECEIVING_TOKEN_ACCOUNT = await getTokenAccountFromWalletAddress(
                            prismaxRecevingAddress,
                            usdtTokenAddress
                        );
                    }
                    await handleSolanaTransaction(RECEIVING_TOKEN_ACCOUNT);
                } else if (isEvmChain(selectedChain)) {
                    await handleEVMTransaction();
                }
            } catch (err) {
                console.error('Error processing transaction:', err);
            }
        };
        if (transactionTrigger !== 0) {
            run();
        }
    }, [transactionTrigger]);

    useEffect(() => {
        if (connectWalletModalRequest === 0) {
            return;
        }

        setShowPanel(false);
        setOpenWalletListOnConnectModalOpen(true);
        setForceWalletConnectModalView(Boolean(connectWalletModalForceWalletList));
        setIsConnectModalOpen(true);
    }, [connectWalletModalForceWalletList, connectWalletModalRequest, setShowPanel]);

    // detect  Wallet extension exist?
    useEffect(() => {
        // Check for OKX wallet - it should be detected if either Solana or Ethereum provider exists
        if (window.okxwallet && (window.okxwallet.solana || window.okxwallet.request)) {
            setIsOkxDetected(true);
        }
        // Check for Coinbase Wallet in various injection points
        if (window.coinbaseWalletExtension ||
            window.coinbaseSolana ||
            (window.solana && window.solana.isCoinbaseWallet) ||
            (window.ethereum && window.ethereum.isCoinbaseWallet)) {
            setIsCoinbaseDetected(true);
        }
        let phantomProvider = window.phantom?.solana || window.solana;
        if (phantomProvider && phantomProvider.isPhantom) {
            setIsPhantomDetected(true);
        }
        if (window.ethereum && window.ethereum.isMetaMask) {
            setIsMetaMaskDetected(true);
        } else {
            getWalletStandardWallets()
                .then((wallets) => {
                    if (wallets.some((wallet) => wallet?.name?.toLowerCase().includes('metamask'))) {
                        setIsMetaMaskDetected(true);
                    }
                })
                .catch((error) => {
                    console.warn('wallet-standard detection failed:', error);
                });
        }
    }, []);

    // Default to Base chain when running inside the Base mini app
    useEffect(() => {
        let isCancelled = false;

        const detectMiniApp = async () => {
            try {
                const inMiniApp = await sdk.isInMiniApp();

                if (isCancelled) {
                    return;
                }

                setIsBaseMiniApp(inMiniApp);

                if (inMiniApp) {
                    if (typeof window !== 'undefined' && window.localStorage) {
                        window.localStorage.setItem('selectedChain', 'base');
                        window.localStorage.setItem('lastConnectedChain', 'base');
                    }

                    setSelectedChain((currentChain) => {
                        if (currentChain === 'base') {
                            return currentChain;
                        }
                        return 'base';
                    });
                }
            } catch (error) {
                if (!isCancelled) {
                    console.warn('Base mini app detection failed:', error);
                }
            }
        };

        detectMiniApp();

        return () => {
            isCancelled = true;
        };
    }, [setSelectedChain]);

    useEffect(() => {
        if (isBaseMiniApp) {
            if (showPanel) {
                setShowPanel(false);
            }
        }
    }, [isBaseMiniApp, showPanel, setShowPanel]);

    // Base mini app: auto-connect to Base via Coinbase without user click
    useEffect(() => {
        if (!isBaseMiniApp || baseAutoConnectAttempted) {
            return;
        }

        if (connectedPublicKey) {
            setBaseAutoConnectAttempted(true);
            return;
        }

        const connectBaseWallet = async () => {
            try {
                if (typeof window !== 'undefined' && window.localStorage) {
                    window.localStorage.setItem('selectedChain', 'base');
                    window.localStorage.setItem('lastConnectedChain', 'base');
                }

                setSelectedChain((currentChain) => currentChain === 'base' ? currentChain : 'base');
                await handleCoinbaseEVM('base');
            } catch (error) {
                console.error('Automatic Coinbase connection failed in Base mini app:', error);
                showNotification('error', 'Failed to connect via Coinbase Wallet. Please try again.');
            } finally {
                setBaseAutoConnectAttempted(true);
            }
        };

        connectBaseWallet();
    }, [isBaseMiniApp, baseAutoConnectAttempted, connectedPublicKey, setSelectedChain]);


    // Read local storage for last connected wallet - wait for reCAPTCHA to be ready
    useEffect(() => {
        // Only proceed with auto-reconnection if reCAPTCHA is ready and we haven't attempted yet
        if (!isRecaptchaReady || autoReconnectionAttempted) {
            return;
        }

        setAutoReconnectionAttempted(true); // Mark that we've attempted reconnection

        const lastWallet = localStorage.getItem('lastConnectedWallet');
        const lastChain = localStorage.getItem('lastConnectedChain') || 'solana';
        setSelectedChain(lastChain);
        const isEmailLogin = localStorage.getItem('emailUserWalletAddress');
        if (isEmailLogin) {
            // (No action needed: email is already connected)
            fetchThreeTokenBalancesWithoutProvider(isEmailLogin, onWalletConnected);

        } else if (lastWallet === 'okx' && lastChain === 'solana') {
            handleOKXWalletSolana();
        } else if (lastWallet === 'okx' && lastChain !== 'solana') {
            handleOKXEVM(lastChain);
        } else if (lastWallet === 'coinbase' && lastChain === 'solana') {
            handleCoinbaseWalletSolana();
        } else if (lastWallet === 'coinbase' && lastChain !== 'solana') {
            handleCoinbaseEVM(lastChain);
        } else if (lastWallet === 'phantom' && lastChain === 'solana') {
            handlePhantomWalletSolana();
        } else if (lastWallet === 'phantom' && lastChain !== 'solana') {
            handlePhantomEVM(lastChain);
        } else if (lastWallet === 'walletconnect' && lastChain === 'solana') {
            handleWalletConnectSolana();
        } else if (lastWallet === 'walletconnect' && lastChain !== 'solana') {
            handleWalletConnectEVM(lastChain);
        } else if (lastWallet === 'metamask' && lastChain === 'solana') {
            handleMetaMaskSolana();
        } else if (lastWallet === 'metamask' && lastChain !== 'solana') {
            handleMetaMaskEVM(lastChain);
        }
    }, [isRecaptchaReady, autoReconnectionAttempted]); // Add both dependencies

    // Disconnect wallet when chain changes (if wallet is connected)
    useEffect(() => {
        if (!connectedPublicKey || autoReconnectionAttempted === false) {
            return;
        }

        const lastConnectedChain = localStorage.getItem('lastConnectedChain');

        if (lastConnectedChain && lastConnectedChain !== selectedChain) {
            console.log(`Chain switched from ${lastConnectedChain} to ${selectedChain}, disconnecting wallet`);
            disconnectWallet();
            showNotification('info', `Switched to ${getChainName(selectedChain)}. Please reconnect your wallet.`);
        }
    }, [selectedChain]); // Only trigger when selectedChain changes

    // -----------------------------------------
    // UTILITY
    // -----------------------------------------
    const handleConnectWalletClick = async (e) => {
        e.stopPropagation();

        if (isBaseMiniApp) {
            setShowPanel(false);

            if (typeof window !== 'undefined' && window.localStorage) {
                window.localStorage.setItem('selectedChain', 'base');
                window.localStorage.setItem('lastConnectedChain', 'base');
            }

            setSelectedChain((currentChain) => {
                if (currentChain === 'base') {
                    return currentChain;
                }
                return 'base';
            });

            try {
                await handleCoinbaseEVM('base');
            } catch (error) {
                console.error('Automatic Coinbase connection failed in Base mini app:', error);
                showNotification('error', 'Failed to connect via Coinbase Wallet. Please try again.');
            }
            return;
        }

        setShowPanel(false);
        setOpenWalletListOnConnectModalOpen(false);
        setForceWalletConnectModalView(false);
        setIsConnectModalOpen(true);
    };
    const closePanel = () => setShowPanel(false);
    const stopPropagation = (e) => e.stopPropagation();
    const handleOutsideClick = () => {
        if (showPanel) closePanel();
    };
    const handleMenuIconClick = (e) => {
        e.stopPropagation();
        setIsSideMenuOpen(true);
    };
    const handleOutsideClickSideMenu = () => {
        setIsSideMenuOpen(false);
    };
    const handleMenuItemClick = (item) => {
        setSelectedMenuItem(item);
        setIsSideMenuOpen(false);
    };
    const handleLoginWithEmail = () => {
        setShowPanel(false);
        setOpenWalletListOnConnectModalOpen(false);
        setForceWalletConnectModalView(false);
        setIsConnectModalOpen(true);
    };
    const handleLogoutWithEmail = async () => {
        if (await logout()) {
            showNotification('success', "You have successfully disconnected.");

        } else {
            showNotification('error', "An error occurred while disconnecting your email account. Please try again.");
        }
        setWalletAddress(CONNECT_DEFAULT_TEXT)
    };

    function shortenAddress(address, startLength = 4, endLength = 4) {
        if (!address) return '';
        if (address.length <= startLength + endLength) {
            return address;
        }
        const start = address.slice(0, startLength);
        const end = address.slice(-endLength);
        return `${start}..${end}`;
    }

    function isMobile() {
        return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    }

    // This function is used for wallet connections (not email connections)
    const fetchUserInfoAfterConnect = async (walletAddress, chainOverride = null) => {
        try {
            if (!isRecaptchaReady) {
                return false;
            }
            const recaptchaToken = await executeRecaptchaAction('fetch_user_info');
            const localEmail = localStorage.getItem('userEmail');
            const localToken = localStorage.getItem('gatewayToken');

            const params = new URLSearchParams();
            params.append('wallet_address', walletAddress);
            params.append('recaptcha_token', recaptchaToken);
            params.append('chain', chainOverride || selectedChain);

            const urlParams = new URLSearchParams(window.location.search);
            const referralCode = urlParams.get('ref');
            const acquisitionSource = urlParams.get('source');

            if (referralCode) {
                params.append('referrers_referral_code', referralCode);
            }

            if (acquisitionSource === 'galxe') {
                params.append('source', 'galxe');
            }


            if (localEmail && localToken) {
                params.append('email', localEmail);
                params.append('token', localToken);
            }
            const response = await fetch(`${PRISMAX_BACKEND_URL}/api/get-users?${params}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            if (response.ok) {
                const result = await response.json();

                if (result.wallet_binding_blocked) {
                    showNotification('warning', 'The wallet has been registered please use a different wallet or login directly with your wallet. Unlink your email from your wallet in the Account page.', 15000);
                    return false;
                }

                if (result.success && result.data) {
                    if (result.data.user_class) {
                        localStorage.setItem('userClass', result.data.user_class);
                        setUserClass(result.data.user_class)
                    }
                    const nextUserRole = getAccountRoleFromUserData(result.data);
                    const nextQaRole = getQaRoleFromUserData(result.data);
                    setUserRole(nextUserRole);
                    setQaRole(nextQaRole);
                    if (nextUserRole) localStorage.setItem('userRole', nextUserRole);
                    else localStorage.removeItem('userRole');
                    if (nextQaRole) localStorage.setItem('qaRole', nextQaRole);
                    else localStorage.removeItem('qaRole');
                    if (result.data.hash_code) {
                        localStorage.setItem('gatewayToken', result.data.hash_code);
                        setGatewayToken(result.data.hash_code)
                    }
                    if (result.data.userid) {
                        localStorage.setItem('userId', result.data.userid);
                        setUserId(result.data.userid)
                    }
                    if (result.data.total_points) {
                        localStorage.setItem("totalPoints", result.data.total_points);
                    }
                    if (result.data.email) {
                        localStorage.setItem('userEmail', result.data.email);
                        setUserEmail(result.data.email)
                    }
                    if (result.data.referral_code) {
                        setReferralCode(result.data.referral_code);
                    }
                    if (result.wallet_member && result.wallet_member !== 'Explorer Member') {
                        showNotification('warning', 'The wallet has been registered please use a different wallet or login directly with your wallet. Unlink your email from your wallet in the Account page.', 15000)
                    }
                    return true
                }
            } else {
                const errorResult = await response.json().catch(() => ({}));
                console.log('Error response from server:', {
                    status: response.status,
                    error: errorResult,
                    chain: chainOverride || selectedChain,
                    wallet: walletAddress
                });

                if (response.status === 400) {
                    console.error('Binding conflict:', errorResult.msg);
                    showNotification(
                        'error',
                        'This email or wallet address is already linked. Please unlink it before attempting to bind another.'
                    );
                    setShowPanel(false)
                    return false
                } else if (response.status === 403) {
                    showNotification(
                        'error',
                        'Too many login attempts detected. Please wait a moment and try again.'
                    );
                    setShowPanel(false)
                    return false
                } else if (response.status === 402) {
                    // 402 typically indicates insufficient balance
                    const chainForMessage = chainOverride || selectedChain;
                    const currencyName = isEvmChain(chainForMessage)
                        ? getNativeCurrencyDisplayName(chainForMessage)
                        : 'SOL';
                    showNotification(
                        'error',
                        `Please connect a wallet with a positive ${currencyName} balance or log in with your email to continue.`
                    );
                    return false
                } else if (response.status === 500) {
                    const chainForMessage = chainOverride || selectedChain;
                    const currencyName = isEvmChain(chainForMessage)
                        ? getNativeCurrencyDisplayName(chainForMessage)
                        : 'SOL';
                    showNotification(
                        'error',
                        `Please connect a wallet with a positive ${currencyName} balance or log in with your email to continue.`
                    );
                    return false
                } else {
                    // For other errors, show a more generic message
                    showNotification(
                        'error',
                        errorResult.msg || 'Failed to connect wallet. Please try again later.'
                    );
                    return false
                }
            }
        } catch (error) {
            showNotification(
                'error',
                'The server is currently busy. Please try again later.'
            );
            console.error('Error fetching user info:', error);
            return false
        }
    };
    // -----------------------------------------
    // OKX CONNECT & FETCH
    // -----------------------------------------
    const handleOKXWalletSolana = async () => {
        try {
            const provider = window.okxwallet?.solana;
            if (!provider) {
                if (isMobile()) {
                    const encodedDappUrl = encodeURIComponent(APP_URL);
                    const deepLink = "okx://wallet/dapp/url?dappUrl=" + encodedDappUrl;

                    const storeUrl = "https://www.okx.com/download";
                    const start = Date.now();
                    setTimeout(() => {
                        const elapsed = Date.now() - start;
                        if (elapsed < 1500) {
                            window.location.href = storeUrl;
                        }
                    }, 1500);
                    window.location.href = "https://www.okx.com/download?deeplink=" + encodeURIComponent(deepLink);
                    return;
                } else {
                    window.open('https://web3.okx.com/zh-hans/download', '_blank');
                    return
                }
            }
            await provider.connect();
            const publicKey = provider.publicKey?.toString();
            if (!publicKey) {
                showNotification('error', 'Unable to retrieve Solana public key from OKX Wallet.');
                return;
            }

            // Check whitelist and request signature for admin
            if (requireSignature) {
                if (adminWhitelist.length > 0 && !adminWhitelist.includes(publicKey)) {
                    showNotification('error', 'This wallet is not authorized for admin access.');
                    await provider.disconnect();
                    return;
                }

                try {
                    const adminSignedSignature = await requestSignature(provider, publicKey);

                    const r = await fetch(`${PRISMAX_BACKEND_URL}/api/admin-login`, {
                        method: "POST",
                        headers: {"Content-Type": "application/json"},
                        body: JSON.stringify({wallet_address: publicKey, signature: adminSignedSignature}),
                    });
                    if (!r.ok) throw new Error("Admin login failed");
                    applyAdminSession(await r.json());
                } catch (error) {
                    showNotification('error', 'Signature verification failed. Admin access denied.');
                    await provider.disconnect();
                    return;
                }
            }
            setShowPanel(false)

            // const ok = await fetchBalancesAndNotifyParent(
            //     provider,
            //     publicKey,
            //     onWalletConnected,
            //     showNotification
            // );
            //
            // if (!ok) {
            //     await provider.disconnect?.();
            //     return;
            // }
            const connect_result = await fetchUserInfoAfterConnect(publicKey, 'solana');
            if (connect_result) {
                setConnectedWalletType("okx");
                localStorage.setItem('lastConnectedWallet', 'okx');
                localStorage.setItem('lastConnectedChain', 'solana');
                setSelectedChain('solana');
                setWalletAddress(shortenAddress(publicKey));
                localStorage.setItem('walletAddress', publicKey);
                setConnectedPublicKey(publicKey);
                setOkxSolanaProvider(provider);
                setShowPanel(false);
            } else {
                // showNotification('error', 'Server busy. Please try again later.')
                await provider.disconnect?.();
            }
        } catch (error) {
            console.error('Error connecting to OKX Wallet (Solana):', error);
        }
    };

    // -----------------------------------------
    // COINBASE CONNECT & FETCH
    // -----------------------------------------
    const handleCoinbaseWalletSolana = async () => {
        try {
            // Coinbase Wallet can inject in different ways
            const provider = window.coinbaseWalletExtension?.solana ||
                window.coinbaseSolana ||
                (window.solana && window.solana.isCoinbaseWallet ? window.solana : null);
            if (!provider) {
                if (isMobile()) {
                    const ua = navigator.userAgent;
                    const isIOS = /iPhone|iPad|iPod/i.test(ua);

                    let coinbaseLink = "";
                    if (isIOS) {
                        coinbaseLink = `cbwallet://dapp?url=${encodeURIComponent(APP_URL)}`;
                    } else {
                        coinbaseLink = `https://go.cb-w.com/dapp?cb_url=${encodeURIComponent(APP_URL)}`;
                    }

                    const storeUrl = "https://www.coinbase.com/en-ca/wallet/downloads";
                    const start = Date.now();
                    setTimeout(() => {
                        const elapsed = Date.now() - start;
                        if (elapsed < 1500) {
                            window.location.href = storeUrl;
                        }
                    }, 1500);

                    window.location.href = coinbaseLink;
                    return;
                } else {
                    window.open('https://www.coinbase.com/wallet', '_blank');
                    return;
                }

            }
            await provider.connect();
            const publicKey = provider.publicKey?.toString();
            if (!publicKey) {
                showNotification('error', 'Unable to retrieve Solana public key from Coinbase Wallet.');
                return;
            }

            // Check whitelist and request signature for admin
            if (requireSignature) {
                if (adminWhitelist.length > 0 && !adminWhitelist.includes(publicKey)) {
                    showNotification('error', 'This wallet is not authorized for admin access.');
                    await provider.disconnect();
                    return;
                }

                try {
                    const adminSignedSignature = await requestSignature(provider, publicKey);

                    const r = await fetch(`${PRISMAX_BACKEND_URL}/api/admin-login`, {
                        method: "POST",
                        headers: {"Content-Type": "application/json"},
                        body: JSON.stringify({wallet_address: publicKey, signature: adminSignedSignature}),
                    });
                    if (!r.ok) throw new Error("Admin login failed");
                    applyAdminSession(await r.json());
                } catch (error) {
                    showNotification('error', 'Signature verification failed. Admin access denied.');
                    await provider.disconnect();
                    return;
                }
            }
            setShowPanel(false)

            // const ok = await fetchBalancesAndNotifyParent(
            //     provider,
            //     publicKey,
            //     onWalletConnected,
            //     showNotification
            // );
            //
            // if (!ok) {
            //     await provider.disconnect?.();
            //     return;
            // }
            const connect_result = await fetchUserInfoAfterConnect(publicKey, 'solana');
            if (connect_result) {
                setConnectedWalletType("coinbase");
                localStorage.setItem('lastConnectedWallet', 'coinbase');
                localStorage.setItem('lastConnectedChain', 'solana');
                setSelectedChain('solana');
                setWalletAddress(shortenAddress(publicKey));
                localStorage.setItem('walletAddress', publicKey);
                setConnectedPublicKey(publicKey);
                setCoinbaseSolanaProvider(provider);
                setShowPanel(false);
            } else {
                // showNotification('error', 'Server busy. Please try again later.')
                await provider.disconnect?.();
            }

        } catch (error) {
            console.error('Error connecting to Coinbase Wallet (Solana):', error);
        }
    };

    // -----------------------------------------
    // WALLETCONNECT CONNECT & FETCH
    // -----------------------------------------
    const handleWalletConnectSolana = async () => {
        try {
            setShowPanel(false);
            await open({view: 'Connect'}); // Opens the WalletConnect modal
            await new Promise(resolve => setTimeout(resolve, 1000));
            if (modalAddress) {

                if (walletProvider) {
                    setShowPanel(false)
                    // const ok = await fetchBalancesAndNotifyParent(
                    //     walletProvider,
                    //     modalAddress,
                    //     onWalletConnected,
                    //     showNotification
                    // );
                    //
                    // if (!ok) {
                    //     await walletProvider.disconnect?.();
                    //     return;
                    // }
                    const connect_result = await fetchUserInfoAfterConnect(modalAddress, 'solana');
                    if (connect_result) {
                        setConnectedWalletType("walletconnect");
                        localStorage.setItem('lastConnectedWallet', 'walletconnect');
                        localStorage.setItem('lastConnectedChain', 'solana');
                        setSelectedChain('solana');
                        localStorage.setItem('walletAddress', modalAddress);

                        setWalletAddress(shortenAddress(modalAddress));

                        setConnectedPublicKey(modalAddress);
                        setWalletConnectProvider(walletProvider);
                    } else {
                        // showNotification('error', 'Server busy. Please try again later.')
                        await walletProvider.disconnect?.();
                    }
                }
                setShowPanel(false);
            } else {
                showNotification('warning', 'WalletConnect connection failed: Please try again.');
            }
        } catch (error) {
            console.error('WalletConnect error:', error);
        }
    };

    const getPhantomProvider = () => {
        let provider = window.phantom?.solana;
        if (!provider) {
            provider = window.solana;
        }
        if (provider && provider.isPhantom) {
            return provider;
        }
        // window.open('https://phantom.app/', '_blank');
        return null;
    };
    const handlePhantomWalletSolana = async () => {
        try {
            const provider = getPhantomProvider();
            if (!provider) {
                if (isMobile()) {
                    const storeUrl = "https://phantom.com/download";
                    const phantomLink = `https://phantom.app/ul/browse/${encodeURIComponent(APP_URL)}?ref=${encodeURIComponent(APP_URL)}`;

                    // 1.5 s still here -> phantom not downloaded
                    const start = Date.now();
                    setTimeout(() => {
                        const elapsed = Date.now() - start;
                        if (elapsed < 1500) {
                            window.location.href = storeUrl;
                        }
                    }, 1500);
                    window.location.href = phantomLink;

                    return;
                } else {
                    window.open('https://phantom.com/', '_blank');
                    return;
                }
            }
            // Request connection to the Phantom Wallet
            await provider.connect();
            const publicKey = provider.publicKey?.toString();
            if (!publicKey) {
                showNotification('error', 'Unable to retrieve Solana public key from Phantom Wallet.');
                return;
            }
            // Check whitelist and request signature for admin
            if (requireSignature) {
                if (adminWhitelist.length > 0 && !adminWhitelist.includes(publicKey)) {
                    showNotification('error', 'This wallet is not authorized for admin access.');
                    await provider.disconnect();
                    return;
                }
                
                try {
                    const adminSignedSignature = await requestSignature(provider, publicKey);

                    const r = await fetch(`${PRISMAX_BACKEND_URL}/api/admin-login`, {
                        method: "POST",
                        headers: {"Content-Type": "application/json"},
                        body: JSON.stringify({wallet_address: publicKey, signature: adminSignedSignature}),
                    });
                    if (!r.ok) throw new Error("Admin login failed");
                    applyAdminSession(await r.json());
                } catch (error) {
                    showNotification('error', 'Signature verification failed. Admin access denied.');
                    await provider.disconnect();
                    return;
                }
            }
            // Update state with Phantom connection details
            setShowPanel(false)
            // const ok = await fetchBalancesAndNotifyParent(
            //     provider,
            //     publicKey,
            //     onWalletConnected,
            //     showNotification
            // );

            // if (!ok) {
            //     await provider.disconnect?.();
            //     return;
            // }
            const connect_result = await fetchUserInfoAfterConnect(publicKey, 'solana');
            if (connect_result) {
                setConnectedWalletType("phantom");
                localStorage.setItem('lastConnectedWallet', 'phantom');
                localStorage.setItem('lastConnectedChain', 'solana');
                setSelectedChain('solana');
                setWalletAddress(shortenAddress(publicKey));
                localStorage.setItem('walletAddress', publicKey);
                setConnectedPublicKey(publicKey);
                setPhantomSolanaProvider(provider);
                setShowPanel(false);
            } else {
                // showNotification('error', 'Server busy. Please try again later.')
                await provider.disconnect?.();
            }

        } catch (error) {
            console.error('Error connecting to Phantom Wallet (Solana):', error);
        }
    };

    const selectInjectedProvider = (matcher) => {
        if (window.ethereum?.providers?.length) {
            const match = window.ethereum.providers.find(matcher);
            if (match) return match;
        }
        if (window.ethereum && matcher(window.ethereum)) {
            return window.ethereum;
        }
        return null;
    };

    const normalizeChainId = (chainId) => {
        if (!chainId) return null;
        if (typeof chainId === 'number') {
            return `0x${chainId.toString(16)}`.toLowerCase();
        }
        const trimmed = chainId.toString().trim();
        if (trimmed.startsWith('0x') || trimmed.startsWith('0X')) {
            return `0x${trimmed.slice(2).toLowerCase()}`;
        }
        const decimal = Number(trimmed);
        if (!Number.isNaN(decimal)) {
            return `0x${decimal.toString(16)}`.toLowerCase();
        }
        return trimmed.toLowerCase();
    };

    // Generic EVM connection handler
    const handleEVMWalletConnection = async (walletType, provider, chainOverride = null) => {
        try {
            const targetChain = chainOverride || selectedChain;
            console.log('handleEVMWalletConnection:', {walletType, targetChain, chainOverride, selectedChain});

            if (!isEvmChain(targetChain)) {
                showNotification('error', 'This wallet does not support Solana. Please use a Solana wallet.');
                return;
            }

            const chainConfig = getEvmChainConfig(targetChain);
            if (!chainConfig) {
                showNotification('error', 'Unsupported chain selection. Please try again.');
                return;
            }

            const targetChainId = normalizeChainId(chainConfig.chainId);

            // For Coinbase Wallet, skip the permissions request as it's not supported
            let accounts;
            if (walletType === 'coinbase') {
                // For Monad chain coinbase wallet extension, since the app was developed when monad is not on public
                // mainnet, so we need to reset the wallet account everytime user tries to connect to avoid bug
                if (targetChainId === '0x8f') { // Monad
                    const solanaProvider = window.coinbaseWalletExtension?.solana ||
                        window.coinbaseSolana ||
                        (window.solana && window.solana.isCoinbaseWallet ? window.solana : null);

                    if (solanaProvider?.connect) {
                        try {
                            console.log('Triggering Coinbase Solana pre-check before Monad connection');
                            await solanaProvider.connect();
                            await solanaProvider.disconnect?.();
                        } catch (solanaError) {
                            console.error('Error connecting to Coinbase Wallet (Solana):', solanaError);
                        }
                    }
                }
                try {
                    accounts = await provider.request({method: 'eth_requestAccounts'});
                } catch (err) {
                    console.error("eth_requestAccounts error:", err);
                }
            } else {
                // For other wallets, force account selection popup
                try {
                    await provider.request({
                        method: 'wallet_requestPermissions',
                        params: [{
                            eth_accounts: {}
                        }]
                    });
                } catch (permError) {
                    console.log('Permission request failed, trying direct account request:', permError);
                }
                // Get the selected accounts
                accounts = await provider.request({method: 'eth_requestAccounts'});
            }
            if (!accounts || accounts.length === 0) {
                showNotification('error', `No accounts found in ${walletType}.`);
                return;
            }

            const address = accounts[0];

            // Switch to the correct chain
            let currentChainId = null;

            try {
                currentChainId = normalizeChainId(await provider.request({method: 'eth_chainId'}));
            } catch (chainIdError) {
                console.log('Unable to read current chain id before switching:', chainIdError);
            }

            const needsSwitch = !currentChainId || currentChainId !== targetChainId;
            try {
                if (needsSwitch) {
                    await provider.request({
                        method: 'wallet_switchEthereumChain',
                        params: [{chainId: targetChainId}],
                    });
                }
            } catch (switchError) {
                if (switchError.code === 4902) {
                    // Network not found, try to add it

                    try {
                        await provider.request({
                            method: 'wallet_addEthereumChain',
                            params: [{
                                chainId: targetChainId,
                                chainName: chainConfig.label,
                                nativeCurrency: chainConfig.nativeCurrency,
                                rpcUrls: chainConfig.rpcUrls,
                                blockExplorerUrls: chainConfig.blockExplorerUrls
                            }]
                        });
                        // Try switching again after adding
                        await provider.request({
                            method: 'wallet_switchEthereumChain',
                            params: [{chainId: targetChainId}],
                        });
                    } catch (addError) {
                        // showNotification('error', `Failed to add ${chainConfig.label} network to wallet.`);
                        // return;
                    }
                } else if (switchError.code === 4001) {
                    showNotification('error', `Please approve the network switch to ${chainConfig.label} in your wallet.`);
                    return;
                } else {
                    // Coinbase Wallet can throw method-not-found for custom networks; continue if chain is already correct
                    const latestChainId = normalizeChainId(await provider.request({method: 'eth_chainId'}).catch(() => null));
                    if (latestChainId !== targetChainId) {
                        const walletName = walletType === 'coinbase' ? 'Coinbase Wallet' : 'your wallet';
                        showNotification('error', `Please switch to ${chainConfig.label} in ${walletName} and try again.`);
                        return;
                    }
                }
            }

            // Check for admin signature if required
            if (requireSignature) {
                if (adminWhitelist.length > 0 && !adminWhitelist.includes(address)) {
                    showNotification('error', 'This wallet is not authorized for admin access.');
                    return;
                }

                try {
                    const message = "Sign this message to authenticate as admin for PrismaX AI Admin Portal.";
                    const signature = await provider.request({
                        method: 'personal_sign',
                        params: [message, address],
                    });

                    const r = await fetch(`${PRISMAX_BACKEND_URL}/api/admin-login`, {
                        method: "POST",
                        headers: {"Content-Type": "application/json"},
                        body: JSON.stringify({
                            wallet_address: address,
                            signature: signature,
                            chain: targetChain
                        }),
                    });
                    if (!r.ok) throw new Error("Admin login failed");
                    applyAdminSession(await r.json());
                } catch (error) {
                    showNotification('error', 'Signature verification failed. Admin access denied.');
                    return;
                }
            }

            setShowPanel(false);

            // First, let's check the balance before calling fetchUserInfoAfterConnect
            if (walletType === 'okx') {
                console.log(`Checking OKX wallet balance on ${chainConfig.label} chain...`);
                try {
                    const balanceWei = await provider.request({
                        method: 'eth_getBalance',
                        params: [address, 'latest']
                    });
                    const balance = parseInt(balanceWei, 16) / 1e18;
                    console.log(`${chainConfig.label} balance:`, balance, chainConfig.nativeCurrency?.symbol || 'ETH');
                } catch (err) {
                    console.error('Error checking balance:', err);
                }
            }

            const connect_result = await fetchUserInfoAfterConnect(address, targetChain);
            if (connect_result) {
                setConnectedWalletType(walletType);
                localStorage.setItem('lastConnectedWallet', walletType);
                localStorage.setItem('lastConnectedChain', targetChain);
                setSelectedChain(targetChain); // Sync selectedChain state
                setWalletAddress(shortenAddress(address));
                localStorage.setItem('walletAddress', address);
                setConnectedPublicKey(address);
                setShowPanel(false);

                // EVM balances will be fetched when needed for transactions
                if (onWalletConnected) {
                    onWalletConnected(address, 0, 0, 0);
                }
            }
        } catch (error) {
            console.error(`Error connecting to ${walletType}:`, error);
            showNotification('error', `Failed to connect to ${walletType}.`);
        }
    };

    // MetaMask EVM connection handler
    const handleMetaMaskEVM = async (chainOverride = null) => {
        try {
            const metamaskProvider = selectInjectedProvider((p) => p.isMetaMask && !p.isCoinbaseWallet);
            if (!metamaskProvider) {
                if (isMobile()) {
                    const metamaskLink = `https://metamask.app.link/dapp/${window.location.href}`;
                    window.location.href = metamaskLink;
                } else {
                    window.open('https://metamask.io/download/', '_blank');
                }
                return;
            }
            await handleEVMWalletConnection('metamask', metamaskProvider, chainOverride);
        } catch (error) {
            console.error('Error connecting to MetaMask:', error);
            showNotification('error', 'Failed to connect to MetaMask.');
        }
    };

    // OKX EVM connection handler
    const handleOKXEVM = async (chainOverride = null) => {
        try {
            // OKX wallet injects itself as window.okxwallet and acts as EVM provider directly
            // Based on your log, window.okxwallet has request, send methods and can be used directly
            let okxProvider = null;

            if (window.okxwallet && window.okxwallet.request) {
                // OKX wallet is available and can be used directly as EVM provider
                okxProvider = window.okxwallet;
            } else {
                okxProvider = selectInjectedProvider((p) => p.isOkxWallet);
            }

            if (!okxProvider) {
                if (isMobile()) {
                    const okxLink = `okx://wallet/dapp/details?dappUrl=${encodeURIComponent(window.location.href)}`;
                    window.location.href = okxLink;
                } else {
                    window.open('https://www.okx.com/download', '_blank');
                }
                return;
            }

            await handleEVMWalletConnection('okx', okxProvider, chainOverride);
        } catch (error) {
            console.error('Error connecting to OKX Wallet:', error);
            showNotification('error', 'Failed to connect to OKX Wallet.');
        }
    };

    // Coinbase EVM connection handler
    const handleCoinbaseEVM = async (chainOverride = null) => {
        try {
            // Coinbase Wallet can inject in multiple ways
            const provider = window.coinbaseWalletExtension ||
                selectInjectedProvider((p) => p.isCoinbaseWallet) ||
                (window.ethereum && window.ethereum.isCoinbaseWallet ? window.ethereum : null);

            if (!provider) {
                if (isMobile()) {
                    const coinbaseLink = `https://go.cb-w.com/dapp?cb_url=${encodeURIComponent(window.location.href)}`;
                    window.location.href = coinbaseLink;
                } else {
                    window.open('https://www.coinbase.com/wallet', '_blank');
                }
                return;
            }
            await handleEVMWalletConnection('coinbase', provider, chainOverride);
        } catch (error) {
            console.error('Error connecting to Coinbase Wallet:', error);
            showNotification('error', 'Failed to connect to Coinbase Wallet.');
        }
    };

    // Phantom EVM connection handler
    const handlePhantomEVM = async (chainOverride = null) => {
        try {
            const phantomProvider = window.phantom?.ethereum ||
                selectInjectedProvider((p) => p.isPhantom || p.isPhantomEthereum);

            if (!phantomProvider) {
                if (isMobile()) {
                    const phantomLink = `https://phantom.app/ul/browse/${encodeURIComponent(window.location.href)}?ref=${encodeURIComponent(window.location.href)}`;
                    window.location.href = phantomLink;
                } else {
                    window.open('https://phantom.com/', '_blank');
                }
                return;
            }
            await handleEVMWalletConnection('phantom', phantomProvider, chainOverride);
        } catch (error) {
            console.error('Error connecting to Phantom Wallet:', error);
            showNotification('error', 'Failed to connect to Phantom Wallet.');
        }
    };

    // WalletConnect EVM connection handler
    const handleWalletConnectEVM = async (chainOverride = null) => {
        try {
            const targetChain = chainOverride || selectedChain;
            if (targetChain === 'solana') {
                // If somehow called for Solana, redirect to Solana handler
                return handleWalletConnectSolana();
            }

            // For EVM chains, we'll use a simple redirect to WalletConnect web app
            // This is a common pattern for EVM WalletConnect integration
            const dappUrl = encodeURIComponent(window.location.href);
            const walletConnectUrl = `https://walletconnect.com/redirect?uri=${dappUrl}`;

            if (isMobile()) {
                // On mobile, try to open WalletConnect app
                window.location.href = walletConnectUrl;
            } else {
                // On desktop, show a message or open in new tab
                showNotification('info', 'WalletConnect for EVM chains: Please scan QR code with your wallet app.');
                window.open('https://walletconnect.com/', '_blank');
            }
        } catch (error) {
            console.error('Error with WalletConnect EVM:', error);
            showNotification('error', 'WalletConnect connection failed.');
        }
    };

    // MetaMask Solana connection handler via Wallet Standard
    const handleMetaMaskSolana = async () => {
        try {
            const wallet = await findMetaMaskWalletStandard();
            if (!wallet) {
                if (isMobile()) {
                    const metamaskLink = `https://metamask.app.link/dapp/${window.location.href}`;
                    window.location.href = metamaskLink;
                } else {
                    window.open('https://metamask.io/download/', '_blank');
                }
                return;
            }

            const connectFeature = wallet.features?.['standard:connect'];
            if (!connectFeature?.connect) {
                showNotification('error', 'MetaMask does not expose Wallet Standard connect.');
                return;
            }

            let connectResult;
            try {
                connectResult = await connectFeature.connect();
            } catch (error) {
                if (error?.code === 4001) {
                    showNotification('error', 'User rejected the request.');
                    return;
                }
                throw error;
            }

            const account = getWalletStandardSolanaAccount(wallet, connectResult?.accounts);
            if (!account) {
                showNotification('error', 'No Solana account found. Please enable Solana in MetaMask.');
                return;
            }

            const chain = getWalletStandardSolanaChain(wallet, account);
            const publicKey = account.address || (account.publicKey ? bs58.encode(account.publicKey) : null);
            if (!publicKey) {
                showNotification('error', 'Unable to retrieve Solana public key from MetaMask.');
                return;
            }
            const provider = createWalletStandardSolanaProvider(wallet, account, chain);

            if (requireSignature) {
                if (adminWhitelist.length > 0 && !adminWhitelist.includes(publicKey)) {
                    showNotification('error', 'This wallet is not authorized for admin access.');
                    await provider.disconnect?.();
                    return;
                }

                try {
                    const adminSignedSignature = await requestSignature(provider, publicKey);

                    const r = await fetch(`${PRISMAX_BACKEND_URL}/api/admin-login`, {
                        method: "POST",
                        headers: {"Content-Type": "application/json"},
                        body: JSON.stringify({
                            wallet_address: publicKey,
                            signature: adminSignedSignature,
                            chain: 'solana'
                        }),
                    });
                    if (!r.ok) throw new Error("Admin login failed");
                    applyAdminSession(await r.json());
                } catch (error) {
                    showNotification('error', 'Signature verification failed. Admin access denied.');
                    await provider.disconnect?.();
                    return;
                }
            }

            setShowPanel(false);

            const connect_result = await fetchUserInfoAfterConnect(publicKey, 'solana');
            if (connect_result) {
                setConnectedWalletType("metamask");
                localStorage.setItem('lastConnectedWallet', 'metamask');
                localStorage.setItem('lastConnectedChain', 'solana');
                setSelectedChain('solana');
                setWalletAddress(shortenAddress(publicKey));
                localStorage.setItem('walletAddress', publicKey);
                setConnectedPublicKey(publicKey);
                setMetamaskSolanaProvider(provider);
                setShowPanel(false);
            } else {
                await provider.disconnect?.();
            }
        } catch (error) {
            console.error('Error connecting to MetaMask Solana:', error);
            showNotification('error', 'Failed to connect to MetaMask for Solana.');
        }
    };

    function verifyBalanceBeforeTransaction({
                                                selectedToken,
                                                amount,
                                                usdcBalance,
                                                usdtBalance
                                            }) {
        const needed = Number(amount) || 0;
        if (selectedToken === 'USDC' && needed > usdcBalance) {
            return false;
        }
        if (selectedToken === 'USDT' && needed > usdtBalance) {
            return false;
        }
        return true
    }

    // Get EVM provider based on connected wallet type
    // todo could be bug here need to test
    const getEVMProvider = () => {
        if (connectedWalletType === 'metamask') {
            return selectInjectedProvider((p) => p.isMetaMask) || window.ethereum;
        } else if (connectedWalletType === 'okx') {
            return window.okxwallet || selectInjectedProvider((p) => p.isOkxWallet) || window.ethereum;
        } else if (connectedWalletType === 'coinbase') {
            return window.coinbaseWalletExtension ||
                selectInjectedProvider((p) => p.isCoinbaseWallet) ||
                (window.ethereum && window.ethereum.isCoinbaseWallet ? window.ethereum : null);
        } else if (connectedWalletType === 'phantom') {
            return window.phantom?.ethereum ||
                selectInjectedProvider((p) => p.isPhantom || p.isPhantomEthereum);
        }
        return null;
    };

    const getEVMProviderCandidates = () => {
        const candidates = [
            {
                walletType: 'metamask',
                provider: selectInjectedProvider((p) => p.isMetaMask && !p.isCoinbaseWallet)
            },
            {
                walletType: 'okx',
                provider: (window.okxwallet && window.okxwallet.request)
                    ? window.okxwallet
                    : selectInjectedProvider((p) => p.isOkxWallet)
            },
            {
                walletType: 'coinbase',
                provider: window.coinbaseWalletExtension ||
                    selectInjectedProvider((p) => p.isCoinbaseWallet) ||
                    (window.ethereum && window.ethereum.isCoinbaseWallet ? window.ethereum : null)
            },
            {
                walletType: 'phantom',
                provider: window.phantom?.ethereum ||
                    selectInjectedProvider((p) => p.isPhantom || p.isPhantomEthereum)
            }
        ];
        const seenProviders = new Set();

        return candidates.filter(({provider}) => {
            if (!provider || typeof provider.request !== 'function' || seenProviders.has(provider)) {
                return false;
            }
            seenProviders.add(provider);
            return true;
        });
    };

    const switchEVMProviderToSelectedChain = async (provider) => {
        const chainConfig = getEvmChainConfig(selectedChain);
        if (!chainConfig) {
            return false;
        }

        const targetChainId = normalizeChainId(chainConfig.chainId);
        const currentChainId = normalizeChainId(await provider.request({method: 'eth_chainId'}).catch(() => null));
        if (currentChainId === targetChainId) {
            return true;
        }

        try {
            await provider.request({
                method: 'wallet_switchEthereumChain',
                params: [{chainId: targetChainId}],
            });
            return true;
        } catch (switchError) {
            if (switchError?.code === 4902) {
                try {
                    await provider.request({
                        method: 'wallet_addEthereumChain',
                        params: [{
                            chainId: targetChainId,
                            chainName: chainConfig.label,
                            nativeCurrency: chainConfig.nativeCurrency,
                            rpcUrls: chainConfig.rpcUrls,
                            blockExplorerUrls: chainConfig.blockExplorerUrls
                        }]
                    });
                    await provider.request({
                        method: 'wallet_switchEthereumChain',
                        params: [{chainId: targetChainId}],
                    });
                    return true;
                } catch (addError) {
                    console.error(`Failed to add or switch ${chainConfig.label}:`, addError);
                }
            } else if (switchError?.code === 4001) {
                showNotification('error', `Please approve the network switch to ${chainConfig.label} in your wallet.`);
                return false;
            }

            const latestChainId = normalizeChainId(await provider.request({method: 'eth_chainId'}).catch(() => null));
            if (latestChainId !== targetChainId) {
                showNotification('error', `Please switch to ${chainConfig.label} in your wallet and try again.`);
                return false;
            }
            return true;
        }
    };

    const attachEVMProviderForPayment = async (walletType, provider, address) => {
        const switched = await switchEVMProviderToSelectedChain(provider);
        if (!switched) {
            return null;
        }

        setConnectedWalletType(walletType);
        localStorage.setItem('lastConnectedWallet', walletType);
        localStorage.setItem('lastConnectedChain', selectedChain);
        setWalletAddress(shortenAddress(address));
        localStorage.setItem('walletAddress', address);
        setConnectedPublicKey(address);
        setShowPanel(false);
        return provider;
    };

    const resolveLinkedEVMProviderForPayment = async () => {
        if (!isEvmChain(selectedChain) || !connectedPublicKey || !connectedPublicKey.startsWith('0x')) {
            return null;
        }

        const linkedAddress = connectedPublicKey.toLowerCase();
        const candidates = getEVMProviderCandidates();

        for (const candidate of candidates) {
            const accounts = await candidate.provider.request({method: 'eth_accounts'}).catch(() => []);
            const matchedAddress = accounts?.find((account) => account?.toLowerCase() === linkedAddress);
            if (matchedAddress) {
                return attachEVMProviderForPayment(candidate.walletType, candidate.provider, matchedAddress);
            }
        }

        const preferredWalletType = connectedWalletType || localStorage.getItem('lastConnectedWallet');
        const preferredCandidate = candidates.find((candidate) => candidate.walletType === preferredWalletType);
        const candidateToPrompt = preferredCandidate || candidates[0];

        if (!candidateToPrompt) {
            return null;
        }

        const requestedAccounts = await candidateToPrompt.provider.request({method: 'eth_requestAccounts'}).catch((error) => {
            if (error?.code !== 4001) {
                console.error('eth_requestAccounts error:', error);
            }
            return [];
        });
        const matchedAddress = requestedAccounts?.find((account) => account?.toLowerCase() === linkedAddress);
        if (!matchedAddress) {
            showNotification('error', 'Please select the linked wallet address in your wallet extension.');
            return null;
        }

        return attachEVMProviderForPayment(candidateToPrompt.walletType, candidateToPrompt.provider, matchedAddress);
    };

    const getSolanaProviderCandidates = async () => {
        const candidates = [
            {
                walletType: 'phantom',
                provider: getPhantomProvider()
            },
            {
                walletType: 'okx',
                provider: window.okxwallet?.solana
            },
            {
                walletType: 'coinbase',
                provider: window.coinbaseWalletExtension?.solana ||
                    window.coinbaseSolana ||
                    (window.solana && window.solana.isCoinbaseWallet ? window.solana : null)
            }
        ];

        try {
            const metamaskWallet = await findMetaMaskWalletStandard();
            if (metamaskWallet) {
                candidates.push({
                    walletType: 'metamask',
                    wallet: metamaskWallet,
                    provider: null
                });
            }
        } catch (error) {
            console.warn('Unable to inspect MetaMask Solana wallet standard provider:', error);
        }

        const seenProviders = new Set();
        return candidates.filter(({provider, wallet}) => {
            if (wallet) {
                return true;
            }
            if (!provider || seenProviders.has(provider)) {
                return false;
            }
            seenProviders.add(provider);
            return true;
        });
    };

    const getConnectedSolanaAddress = async (provider) => {
        const publicKey = provider?.publicKey?.toString?.();
        if (publicKey) {
            return publicKey;
        }

        try {
            const response = await provider?.connect?.({onlyIfTrusted: true});
            return response?.publicKey?.toString?.() || provider?.publicKey?.toString?.() || null;
        } catch {
            return null;
        }
    };

    const attachSolanaProviderForPayment = (walletType, provider, address) => {
        setConnectedWalletType(walletType);
        localStorage.setItem('lastConnectedWallet', walletType);
        localStorage.setItem('lastConnectedChain', 'solana');
        setSelectedChain('solana');
        setWalletAddress(shortenAddress(address));
        localStorage.setItem('walletAddress', address);
        setConnectedPublicKey(address);
        setShowPanel(false);

        if (walletType === 'okx') {
            setOkxSolanaProvider(provider);
        } else if (walletType === 'coinbase') {
            setCoinbaseSolanaProvider(provider);
        } else if (walletType === 'phantom') {
            setPhantomSolanaProvider(provider);
        } else if (walletType === 'metamask') {
            setMetamaskSolanaProvider(provider);
        }

        return provider;
    };

    const resolveMetaMaskSolanaProviderForPayment = async (wallet, linkedAddress, shouldPrompt) => {
        const connectFeature = wallet?.features?.['standard:connect'];
        const availableAccounts = wallet?.accounts || [];
        let account = getWalletStandardSolanaAccount(wallet, availableAccounts);

        if (!account && shouldPrompt && connectFeature?.connect) {
            const connectResult = await connectFeature.connect();
            account = getWalletStandardSolanaAccount(wallet, connectResult?.accounts);
        }

        const address = account?.address || (account?.publicKey ? bs58.encode(account.publicKey) : null);
        if (!account || !address || address !== linkedAddress) {
            return null;
        }

        const chain = getWalletStandardSolanaChain(wallet, account);
        return createWalletStandardSolanaProvider(wallet, account, chain);
    };

    const resolveLinkedSolanaProviderForPayment = async () => {
        if (selectedChain !== 'solana' || !connectedPublicKey || connectedPublicKey.startsWith('0x')) {
            return null;
        }

        const linkedAddress = connectedPublicKey;
        const candidates = await getSolanaProviderCandidates();

        for (const candidate of candidates) {
            if (candidate.walletType === 'metamask') {
                const provider = await resolveMetaMaskSolanaProviderForPayment(candidate.wallet, linkedAddress, false);
                if (provider) {
                    return attachSolanaProviderForPayment(candidate.walletType, provider, linkedAddress);
                }
                continue;
            }

            const address = await getConnectedSolanaAddress(candidate.provider);
            if (address === linkedAddress) {
                return attachSolanaProviderForPayment(candidate.walletType, candidate.provider, linkedAddress);
            }
        }

        const preferredWalletType = connectedWalletType || localStorage.getItem('lastConnectedWallet');
        const preferredCandidate = candidates.find((candidate) => candidate.walletType === preferredWalletType);
        const candidateToPrompt = preferredCandidate || candidates[0];

        if (!candidateToPrompt) {
            return null;
        }

        if (candidateToPrompt.walletType === 'metamask') {
            const provider = await resolveMetaMaskSolanaProviderForPayment(candidateToPrompt.wallet, linkedAddress, true);
            if (provider) {
                return attachSolanaProviderForPayment(candidateToPrompt.walletType, provider, linkedAddress);
            }
        } else {
            const response = await candidateToPrompt.provider?.connect?.();
            const address = response?.publicKey?.toString?.() ||
                candidateToPrompt.provider?.publicKey?.toString?.();
            if (address === linkedAddress) {
                return attachSolanaProviderForPayment(candidateToPrompt.walletType, candidateToPrompt.provider, linkedAddress);
            }
        }

        showNotification('error', 'Please select the linked Solana wallet address in your wallet extension.');
        return null;
    };

    // -----------------------------------------
    // TRANSFER LOGIC
    // -----------------------------------------
    const handleSolanaTransaction = async (RECEIVING_TOKEN_ACCOUNT = null) => {
        if (!connectedPublicKey) {
            requestConnectWalletModal?.();
            return;
        }
        let providerToUse = null;
        if (connectedWalletType === 'okx') {
            providerToUse = okxSolanaProvider;
        } else if (connectedWalletType === 'coinbase') {
            providerToUse = coinbaseSolanaProvider;
        } else if (connectedWalletType === 'phantom') {
            providerToUse = phantomSolanaProvider;
        } else if (connectedWalletType === 'metamask') {
            providerToUse = metamaskSolanaProvider;
        } else if (connectedWalletType === 'walletconnect') {
            providerToUse = walletConnectProvider;
        }
        if (!providerToUse) {
            providerToUse = await resolveLinkedSolanaProviderForPayment();
        }
        if (!providerToUse) {
            requestConnectWalletModal?.({forceWalletListView: true});
            setIsProcessing(false);
            return;
        }
        const {
            usdcBalance,
            usdtBalance,
        } = await fetchTokenBalances(providerToUse, connectedPublicKey);
        setUsdtBalance(usdtBalance)
        setUsdcBalance(usdcBalance)
        const verificationResult = verifyBalanceBeforeTransaction({
            selectedToken,
            amount,
            usdcBalance,
            usdtBalance,
        });
        if (!verificationResult) {
            showNotification('warning', `Insufficient ${selectedToken} Balance`);
            setIsProcessing(false)
            return;
        }
        await transferSplToken(connectedPublicKey, providerToUse, selectedToken, null);
    };

    const transferSplToken = async (fromPublicKeyStr, provider, tokenType, C2C_RECEIVING_TOKEN_ACCOUNT) => {
        try {
            const connection = new Connection(RPC_URL, "confirmed");
            const fromPublicKey = new PublicKey(fromPublicKeyStr);
            let tokenMintAddress;
            let toTokenAccountPubkey;
            let readableToken;
            if (tokenType === 'USDT') {
                tokenMintAddress = usdtTokenAddress;
                readableToken = "USDT";
                const toTokenAddress = await getTokenAccountFromWalletAddress(prismaxRecevingAddress, usdtTokenAddress)
                toTokenAccountPubkey = new PublicKey(toTokenAddress);
            } else if (tokenType === 'USDC') {
                tokenMintAddress = usdcTokenAddress;
                readableToken = "USDC";
                const toTokenAddress = await getTokenAccountFromWalletAddress(prismaxRecevingAddress, usdcTokenAddress)
                toTokenAccountPubkey = new PublicKey(toTokenAddress);
            } else {
                showNotification('error', "Unknown token type. Canceling transaction.");
                return;
            }

            let signature;

            // prepare a signer object
            const payer = {
                publicKey: fromPublicKey,
                signTransaction: async (transaction) => {
                    return await provider.signTransaction(transaction);
                },
            };

            const mintPubkey = new PublicKey(tokenMintAddress);
            const fromTokenAccount = await getOrCreateAssociatedTokenAccount(
                connection,
                payer,
                mintPubkey,
                fromPublicKey
            );
            const amountInBaseUnits = Math.floor(Number(amount) * 1_000_000);
            if (isNaN(amountInBaseUnits) || amountInBaseUnits <= 0) {
                showNotification('error', `Invalid ${readableToken} amount. Please provide a positive number.`);
                return;
            }
            // Create the transfer instruction
            const transferIx = createTransferInstruction(
                fromTokenAccount.address,
                toTokenAccountPubkey,
                fromPublicKey,
                amountInBaseUnits
            );

            const memoText = `Send ${amount} ${readableToken} to ${toTokenAccountPubkey.toBase58()}`;
            const memoIx = new TransactionInstruction({
                keys: [],
                programId: new PublicKey('MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr'),
                data: Buffer.from(memoText, 'utf8'),
            });

            // Build the transaction with transfer + memo
            const latestBlockhash = await connection.getLatestBlockhash("confirmed");
            const transaction = new Transaction().add(transferIx).add(memoIx);
            transaction.feePayer = fromPublicKey;
            transaction.recentBlockhash = latestBlockhash.blockhash;

            const result = await provider.signAndSendTransaction(transaction);
            signature = typeof result === 'string' ? result : result?.signature;
            if (signature instanceof Uint8Array) {
                signature = bs58.encode(signature);
            }
            if (!signature) {
                throw new Error('Wallet did not return a transaction signature.');
            }
            console.log("signature", signature);

            const explorerLink = `https://solscan.io/tx/${signature}`;
            showNotification('info', `Transfer of ${amount} ${readableToken} submitted. Waiting for confirmation...
                 <a href="${explorerLink}" target="_blank" rel="noreferrer">Check on Explorer</a>`);

            const confirmation = await connection.confirmTransaction({
                signature,
                blockhash: latestBlockhash.blockhash,
                lastValidBlockHeight: latestBlockhash.lastValidBlockHeight,
            }, "finalized");
            if (confirmation.value?.err) {
                throw new Error('Solana transaction failed during confirmation.');
            }

            showNotification('success', `Transfer of ${amount} ${readableToken} confirmed successfully.
                 <a href="${explorerLink}" target="_blank" rel="noreferrer">Check on Explorer</a>`);

            await fetchBalancesAndNotifyParent(provider, fromPublicKeyStr, onWalletConnected, showNotification, selectedChain);
            try {

                const requestBody = {
                    wallet_address: fromPublicKeyStr,
                    amount_total: amount,
                    user_id: localStorage.getItem('userId'),
                    solana_transaction_hash: signature,
                    currency: selectedToken,
                    chain: selectedChain || 'solana'
                };

                const response = await fetch(`${PRISMAX_BACKEND_URL}/api/record-crypto-payment`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify(requestBody)
                });
                const data = await response.json();
                if (data.success) {
                    onMembershipPaymentSuccess(data.data?.transaction_hash || signature);

                }
                if (!response.ok) {
                    throw new Error(`Server responded with status ${response.status}`);
                }

                // showNotification('success', `Cash received of ${amount} ${selectedToken.toUpperCase()} .
                //          <a href="${explorerLink}" target="_blank" rel="noreferrer">Check on Explorer</a>`);
            } catch (error) {
                showNotification('error', `Upgrading error, please contact support through the Discord ticket channel.`);
            }

        } catch (err) {
            console.error(`Error transferring token on Solana:`, err);
            if (err?.code === 4001) {
                showNotification('error', "User rejected the transaction.");
                setIsProcessing(false)
            } else {
                if (err && err.message) {
                    console.log(" error!", err)
                    showNotification('error', `Transaction failed: ${err.message}`);
                } else {
                    console.log("error! ", err);
                    showNotification('error', 'Transaction failed, please try again');
                }
                setIsProcessing(false)
            }
        }
    };

    // EVM Transaction Handler
    const handleEVMTransaction = async () => {
        if (!connectedPublicKey) {
            requestConnectWalletModal?.();
            return;
        }

        if (!isEvmChain(selectedChain)) {
            showNotification('error', 'Selected chain does not support EVM transactions.');
            return;
        }

        let provider = getEVMProvider();
        if (!provider) {
            provider = await resolveLinkedEVMProviderForPayment();
        }
        if (!provider) {
            requestConnectWalletModal?.({forceWalletListView: true});
            setIsProcessing(false);
            return;
        }

        // Check balances before transaction
        const {usdcBalance, usdtBalance} = await fetchEVMTokenBalances(provider, connectedPublicKey, selectedChain);
        setUsdtBalance(usdtBalance);
        setUsdcBalance(usdcBalance);

        const verificationResult = verifyBalanceBeforeTransaction({
            selectedToken,
            amount,
            usdcBalance,
            usdtBalance,
        });

        if (!verificationResult) {
            showNotification('warning', `Insufficient ${selectedToken} Balance`);
            setIsProcessing(false);
            return;
        }

        await transferERC20Token(connectedPublicKey, provider, selectedToken);
    };

    // ERC20 Token Transfer Function
    const transferERC20Token = async (fromAddress, provider, tokenType) => {
        try {
            const chainConfig = getEvmChainConfig(selectedChain);
            if (!chainConfig) {
                showNotification('error', 'Unsupported EVM chain selected.');
                setIsProcessing(false);
                return;
            }

            const tokenConfig = chainConfig.supportedTokens?.[tokenType];
            if (!tokenConfig?.address) {
                showNotification('error', `${tokenType} is not supported on ${chainConfig.label}.`);
                setIsProcessing(false);
                return;
            }

            const readableToken = tokenType;

            const decimals = tokenConfig.decimals ?? 6;
            const amountInBaseUnits = Math.floor(Number(amount) * Math.pow(10, decimals));
            if (isNaN(amountInBaseUnits) || amountInBaseUnits <= 0) {
                showNotification('error', `Invalid ${readableToken} amount. Please provide a positive number.`);
                setIsProcessing(false);
                return;
            }

            // Convert to hex string
            const amountHex = '0x' + amountInBaseUnits.toString(16);

            // ERC20 transfer function signature: transfer(address,uint256)
            const transferData = '0xa9059cbb' + // transfer function selector
                prismaxEvmReceivingAddress.slice(2).padStart(64, '0') + // to address (32 bytes)
                amountHex.slice(2).padStart(64, '0'); // amount (32 bytes)

            // Send transaction
            const txHash = await provider.request({
                method: 'eth_sendTransaction',
                params: [{
                    from: fromAddress,
                    to: tokenConfig.address,
                    data: transferData,
                    value: '0x0', // No ETH value for ERC20 transfers
                }],
            });

            console.log("Transaction hash:", txHash);

            // Show initial notification
            const explorerBase = getExplorerBaseUrl(selectedChain);
            const explorerUrl = `${explorerBase.replace(/\/$/, '')}/tx/${txHash}`;

            showNotification('info', `Transaction submitted. Waiting for confirmation...
                <a href="${explorerUrl}" target="_blank" rel="noreferrer">View on Explorer</a>`);

            // Wait for transaction confirmation
            const success = await waitForTransactionConfirmation(provider, txHash);

            if (success) {
                showNotification('success', `Transfer of ${amount} ${readableToken} confirmed successfully!
                    <a href="${explorerUrl}" target="_blank" rel="noreferrer">View on Explorer</a>`);

                // Update balances after successful transaction
                await new Promise(resolve => setTimeout(resolve, 3000));
                await fetchEVMBalancesAndNotifyParent(provider, fromAddress, onWalletConnected, showNotification, selectedChain);

                // Record payment
                try {
                    const requestBody = {
                        wallet_address: fromAddress,
                        amount_total: amount,
                        user_id: localStorage.getItem('userId'),
                        solana_transaction_hash: txHash, // backend expects this field name
                        currency: selectedToken,
                        chain: selectedChain
                    };

                    const response = await fetch(`${PRISMAX_BACKEND_URL}/api/record-crypto-payment`, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json"
                        },
                        body: JSON.stringify(requestBody)
                    });

                    const data = await response.json();
                    if (data.success) {
                        onMembershipPaymentSuccess(data.data?.transaction_hash || txHash);
                    }

                    if (!response.ok) {
                        throw new Error(`Server responded with status ${response.status}`);
                    }

                } catch (error) {
                    showNotification('error', `Payment recorded, but upgrade error occurred. Please contact support through Discord.`);
                }
            } else {
                showNotification('error', 'Transaction failed or was not confirmed. Please try again.');
                setIsProcessing(false);
            }

        } catch (err) {
            console.error(`Error transferring ${tokenType} on ${selectedChain}:`, err);
            if (err?.code === 4001) {
                showNotification('error', "User rejected the transaction.");
                setIsProcessing(false);
            } else {
                if (err && err.message) {
                    console.log("Error:", err);
                    showNotification('error', `Transaction failed: ${err.message}`);
                } else {
                    console.log("Error:", err);
                    showNotification('error', 'Transaction failed, please try again');
                }
                setIsProcessing(false);
            }
        }
    };

    // Wait for EVM transaction confirmation
    const waitForTransactionConfirmation = async (provider, txHash, maxRetries = 60) => {
        for (let i = 0; i < maxRetries; i++) {
            try {
                const receipt = await provider.request({
                    method: 'eth_getTransactionReceipt',
                    params: [txHash],
                });

                if (receipt) {
                    // Transaction was mined
                    if (receipt.status === '0x1') {
                        return true; // Success
                    } else {
                        return false; // Failed
                    }
                }

                // Wait 5 seconds before checking again
                await new Promise(resolve => setTimeout(resolve, 5000));
            } catch (error) {
                console.error('Error checking transaction status:', error);
                await new Promise(resolve => setTimeout(resolve, 5000));
            }
        }

        // Timeout after 5 minutes (60 * 5 seconds)
        return false;
    };


    // DISCONNECT
    const disconnectWallet = useCallback(async ({showSuccess = true} = {}) => {
        try {
            // Disconnect Solana wallets
            if (okxSolanaProvider && okxSolanaProvider.disconnect) {
                okxSolanaProvider.disconnect();
            }
            if (coinbaseSolanaProvider && coinbaseSolanaProvider.disconnect) {
                await coinbaseSolanaProvider.disconnect();
            }
            if (walletConnectProvider && walletConnectProvider.disconnect) {
                await walletConnectProvider.disconnect();
            }
            if (phantomSolanaProvider && phantomSolanaProvider.disconnect) {
                await phantomSolanaProvider.disconnect();
            }
            if (metamaskSolanaProvider && metamaskSolanaProvider.disconnect) {
                await metamaskSolanaProvider.disconnect();
            }

            // Disconnect MetaMask (EVM wallets)
            if (connectedWalletType === 'metamask' && window.ethereum) {
                // MetaMask doesn't have a programmatic disconnect method
                // We just clear the local state
                console.log('Disconnecting MetaMask - clearing local state');
            }

            // Reset all wallet states
            setConnectedPublicKey(null);
            setWalletAddress(CONNECT_DEFAULT_TEXT);
            setOkxSolanaProvider(null);
            setCoinbaseSolanaProvider(null);
            setWalletConnectProvider(null);
            setPhantomSolanaProvider(null);
            setMetamaskSolanaProvider(null);
            setShowPanel(false);
            setConnectedWalletType(null);


            if (setAdminLogin) {
                setAdminLogin(false)
            }
            setUserClass('Explorer Member')
            clearLocalSessionData();
            if (onWalletConnected) {
                onWalletConnected(null, 0, 0, 0);
            }
            if (showSuccess) {
                showNotification('success', 'Your wallet has been successfully disconnected.')
            }

        } catch (err) {
            console.error("Error disconnecting wallet:", err);
        }
    }, [
        okxSolanaProvider,
        coinbaseSolanaProvider,
        walletConnectProvider,
        phantomSolanaProvider,
        metamaskSolanaProvider,
        connectedWalletType,
        setConnectedPublicKey,
        setWalletAddress,
        setShowPanel,
        setAdminLogin,
        setUserClass,
        clearLocalSessionData,
        onWalletConnected,
        showNotification,
    ]);

    useEffect(() => {
        const handleSessionExpired = () => {
            void disconnectWallet({showSuccess: false});
        };
        window.addEventListener(USER_SESSION_EXPIRED_EVENT, handleSessionExpired);
        return () => {
            window.removeEventListener(USER_SESSION_EXPIRED_EVENT, handleSessionExpired);
        };
    }, [disconnectWallet]);

    const getChainName = (chain) => {
        if (chain === 'solana') {
            return 'Solana';
        }
        return EVM_CHAIN_CONFIG[chain]?.label || 'Solana';
    };

    const handleChainSelect = (chain) => {
        // Check if user is logged in with email and has a connected wallet
        if (userEmail && connectedPublicKey) {
            showNotification(
                'warning',
                'Cannot switch chains while logged in with email and wallet connected. You can connect with another wallet on the same network.'
            );
            return;
        }

        setSelectedChain(chain);
        localStorage.setItem('selectedChain', chain);
    };

    const coinbaseBadgeIcon = base_chain_icon_svg;
    const coinbaseWalletIconDesktop = base_chain_icon_svg;
    const coinbaseWalletIconMobile = base_chain_icon_svg;
    const handleModalWalletUnlink = useCallback(async () => {
        const token = localStorage.getItem('gatewayToken');
        const email = localStorage.getItem('userEmail') || userEmail;
        const walletAddressToUnlink = localStorage.getItem('walletAddress') || connectedPublicKey;
        const chain = localStorage.getItem('selectedChain') || selectedChain || 'solana';

        if (!token || !email || !walletAddressToUnlink) {
            throw new Error('Missing email session or linked wallet information.');
        }

        const response = await fetch(`${PRISMAX_BACKEND_URL}/api/disconnect-wallet-from-email`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                email,
                wallet_address: walletAddressToUnlink,
                chain
            })
        });
        const result = await response.json().catch(() => ({}));

        if (!response.ok || !result.success) {
            throw new Error(result?.msg || 'Failed to unlink your wallet. Please try again.');
        }

        setConnectedPublicKey('');
        setWalletAddress(CONNECT_DEFAULT_TEXT);
        setConnectedWalletType(null);
        setLinkedWalletChain?.('');
        setOkxSolanaProvider(null);
        setCoinbaseSolanaProvider(null);
        setWalletConnectProvider(null);
        setPhantomSolanaProvider(null);
        setMetamaskSolanaProvider(null);
        setSelectedChain('solana');

        localStorage.removeItem('walletAddress');
        localStorage.removeItem('linkedWalletChain');
        localStorage.removeItem('lastConnectedWallet');
        localStorage.removeItem('lastConnectedChain');
        localStorage.removeItem('selectedChain');

        if (onWalletConnected) {
            onWalletConnected(null, 0, 0, 0);
        }

    }, [
        connectedPublicKey,
        onWalletConnected,
        selectedChain,
        setConnectedPublicKey,
        setLinkedWalletChain,
        setSelectedChain,
        userEmail
    ]);

    const handleModalWalletConnect = async (walletType) => {
        if (selectedChain === 'solana') {
            if (walletType === 'phantom') return handlePhantomWalletSolana();
            if (walletType === 'metamask') return handleMetaMaskSolana();
            if (walletType === 'okx') return handleOKXWalletSolana();
            if (walletType === 'coinbase') return handleCoinbaseWalletSolana();
            if (walletType === 'walletconnect') return handleWalletConnectSolana();
        }

        if (walletType === 'phantom') return handlePhantomEVM(selectedChain);
        if (walletType === 'metamask') return handleMetaMaskEVM(selectedChain);
        if (walletType === 'okx') return handleOKXEVM(selectedChain);
        if (walletType === 'coinbase') return handleCoinbaseEVM(selectedChain);
    };

    return (
        <div
            className={location.pathname === '/invite' ? `${styles.transparentOuterContainer} ${styles.outerContainer}` : styles.outerContainer}
            onClick={handleOutsideClick}
        >
            <img
                className={styles.menu_mobile_icon}
                src={menu_icon}
                style={{width: '22px', height: '22px'}}
                alt="Menu"
                onClick={handleMenuIconClick}
            />
            <img
                className={styles.vectorIcon}
                src={logo_svg}
                alt="PrismaX AI"
            />


            {notification.message && ReactDOM.createPortal(
                <div className={styles.transactionErrorBoxWapper}>
                    <div className={styles.transactionErrorBox}>
                        <div className={styles.transactionErrorContent}>
                            <img
                                src={
                                    notification?.status === 'error'
                                        ? rejectImg
                                        : notification?.status === 'warning'
                                            ? warningImg
                                            : successImg
                                }
                                alt={notification.status || 'status'}
                                className={styles.rejectIcon}
                            />
                            <span
                                className={styles.errorMessage}
                                dangerouslySetInnerHTML={{__html: notification.message}}
                            />
                        </div>
                        <img
                            className={styles.cancelBtn}
                            src={cancle_icon}
                            alt="x"
                            onClick={() => showNotification(null, null)}
                        />
                    </div>
                </div>,
                document.body
            )}
            <div
                    className={location.pathname === '/invite' ? `${styles.transparentContainer} ${styles.container}` : styles.container}>
                    {isSideMenuOpen ? (
                        <SideMenu
                            isOpen={isSideMenuOpen}
                            selectedMenuItem={selectedMenuItem}
                            onMenuItemClick={handleMenuItemClick}
                            onOverlayClick={handleOutsideClickSideMenu}
                            stopPropagation={(e) => e.stopPropagation()}
                        />
                    ) : (
                        <div
                            className={location.pathname === '/invite' ? `${styles.transparentUserIconContainer} ${styles.userIconContainer}` : styles.userIconContainer}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Connect text (pre-login) or user's wallet/email address (post-login) */}
                            <div
                                className={
                                    `${styles.userIcon} ` +
                                    (
                                        walletAddress === CONNECT_DEFAULT_TEXT && !userEmail
                                            ? styles.userIconNoHover
                                            : styles.userIconRedHover
                                    )
                                }
                                onClick={handleConnectWalletClick}
                                style={{
                                    background:
                                        walletAddress === CONNECT_DEFAULT_TEXT && !userEmail
                                            ? "white"
                                            : "#32353D",
                                    color:
                                        walletAddress === CONNECT_DEFAULT_TEXT && !userEmail
                                            ? "black"
                                            : "white",
                                    borderRadius: "10px",
                                    fontSize:
                                        walletAddress === CONNECT_DEFAULT_TEXT && !userEmail
                                            ? "15px"
                                            : undefined,
                                    width: isBaseMiniApp ? '140px' : '151px',
                                    paddingLeft: isBaseMiniApp ? '0px' : 'inherit',
                                    cursor: isBaseMiniApp && connectedPublicKey ? 'default' : 'pointer',
                                    pointerEvents: isBaseMiniApp && connectedPublicKey ? 'none' : 'auto',
                                }}
                            >
                                {userEmail
                                    ? (
                                        <img
                                            style={{ borderRadius: '50%', width: '18px', height: '18px', marginRight: '8px', marginLeft: '0px' }}
                                            src={email_svg}
                                            alt="Email"
                                        />
                                    )
                                    : walletAddress === CONNECT_DEFAULT_TEXT
                                    ? ""
                                    : connectedWalletType === "okx"
                                    ? (
                                        <img
                                            style={{ borderRadius: '50%', width: '18px', height: '18px', marginRight: '8px', marginLeft: '0px' }}
                                            src={okx_icon}
                                            alt="OKX"
                                        />
                                    )
                                    : connectedWalletType === "coinbase"
                                    ? (
                                        <img
                                            style={{ borderRadius: '50%', width: '18px', height: '18px', marginRight: '8px', marginLeft: '0px' }}
                                            src={coinbaseBadgeIcon}
                                            alt="Coinbase"
                                        />
                                    )
                                    : connectedWalletType === "walletconnect"
                                    ? (
                                        <img
                                            style={{ borderRadius: '50%', width: '18px', height: '18px', marginRight: '8px', marginLeft: '0px' }}
                                            src={walletconnect_icon}
                                            alt="WalletConnect"
                                        />
                                    )
                                    : connectedWalletType === "phantom"
                                    ? (
                                        <img
                                            style={{ borderRadius: '50%', width: '18px', height: '18px', marginRight: '8px', marginLeft: '0px' }}
                                            src={Phantom_icon}
                                            alt="Phantom"
                                        />
                                    )
                                    : connectedWalletType === "metamask"
                                    ? (
                                        <img
                                            style={{ borderRadius: '50%', width: '18px', height: '18px', marginRight: '8px', marginLeft: '0px' }}
                                            src={metamask_icon}
                                            alt="MetaMask"
                                        />
                                    )
                                    : null
                                }
                                {userEmail
                                    ? shortenAddress(userEmail) // Email login
                                    : walletAddress // Wallet login (or no login creds found)
                                }
                                {(walletAddress === CONNECT_DEFAULT_TEXT && !userEmail) || isBaseMiniApp ? '' : (
                                    <img
                                        style={{
                                            borderRadius: '50%',
                                            width: '20px',
                                            height: '20px',
                                            marginRight: '0px',
                                            marginLeft: '8px',
                                            background: 'none',
                                            marginTop: '3px',
                                        }}
                                        className={styles.downArrow}
                                        src={menuDropDownImg}
                                        alt="arrow"
                                    />
                                )}
                            </div>


                            {showPanel && (
                                <div
                                    className={styles.overlay}
                                    onClick={handleOutsideClick}
                                ></div>
                            )}

                            <div
                                className={
                                    `${styles.sidePanel} ` +
                                    (showPanel
                                        ? ((connectedPublicKey && !userEmail) ? styles.openConnected : styles.openNotConnected)
                                        : '')
                                }
                                style={{"width": "352px"}}
                                onClick={stopPropagation}
                            >
                                <div className={styles.connectHeader}>
                                    <h3>Connect</h3>
                                    <div
                                        className={styles.closeBtn}
                                        onClick={closePanel}
                                        aria-label="Close"
                                    >
                                        ×
                                    </div>
                                </div>

                                <div className={styles.lineWrapper}>
                                    <div className={styles.dividerLineFull}></div>
                                </div>
                                {/* Chain-specific wallet display logic */}
                                {selectedChain === 'solana' ? (
                                    <>
                                        {/* Solana Wallets - Order: Phantom, MetaMask, OKX, Coinbase, WalletConnect */}
                                        {/* 1. Phantom */}
                                        <div className={styles.connectOption} onClick={handlePhantomWalletSolana}>
                                            <img className={styles.walletIcon}
                                                 src={isMobile ? Phantom_icon_mobile : Phantom_icon}
                                                 alt="Phantom"/>
                                            <div className={styles.walletType}><p>Phantom Wallet</p>
                                                {isPhantomDetected && <div className={styles.detect}>detected</div>}
                                            </div>
                                        </div>

                                        {/* 2. MetaMask */}
                                        <div className={styles.connectOption} onClick={handleMetaMaskSolana}>
                                            <img className={styles.walletIcon}
                                                 src={isMobile ? metamask_icon_mobile : metamask_icon}
                                                 alt="MetaMask"/>
                                            <div className={styles.walletType}><p>MetaMask</p>
                                                {isMetaMaskDetected && <div className={styles.detect}>detected</div>}
                                            </div>
                                        </div>

                                        {/* 3. OKX */}
                                        <div className={styles.connectOption} onClick={handleOKXWalletSolana}>
                                            <img className={styles.walletIcon}
                                                 src={isMobile ? okx_icon_mobile : okx_icon}
                                                 alt="OKX"/>
                                            <div className={styles.walletType}><p>OKX Wallet</p>
                                                {isOkxDetected && <div className={styles.detect}>detected</div>}
                                            </div>
                                        </div>

                                        {/* 4. Coinbase */}
                                        <div className={styles.connectOption} onClick={handleCoinbaseWalletSolana}>
                                            <img className={styles.walletIcon}
                                                 src={isMobile ? coinbaseWalletIconMobile : coinbaseWalletIconDesktop}
                                                 alt="Coinbase"/>
                                            <div className={styles.walletType}><p>Coinbase Wallet</p>
                                                {isCoinbaseDetected && <div className={styles.detect}>detected</div>}
                                            </div>
                                        </div>

                                        {/* 5. WalletConnect */}
                                        <div className={styles.connectOption} onClick={handleWalletConnectSolana}>
                                            <img className={styles.walletIcon}
                                                 src={isMobile ? walletconnect_icon_mobile : walletconnect_icon}
                                                 alt="WalletConnect"/>
                                            <p>WalletConnect</p>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        {/* EVM Wallets - Order: Phantom, MetaMask, OKX, Coinbase */}
                                        {/* 1. Phantom */}
                                        <div className={styles.connectOption}
                                             onClick={() => handlePhantomEVM(selectedChain)}>
                                            <img className={styles.walletIcon}
                                                 src={isMobile ? Phantom_icon_mobile : Phantom_icon}
                                                 alt="Phantom"/>
                                            <div className={styles.walletType}><p>Phantom Wallet</p>
                                                {isPhantomDetected && <div className={styles.detect}>detected</div>}
                                            </div>
                                        </div>

                                        {/* 2. MetaMask */}
                                        <div className={styles.connectOption}
                                             onClick={() => handleMetaMaskEVM(selectedChain)}>
                                            <img className={styles.walletIcon}
                                                 src={isMobile ? metamask_icon_mobile : metamask_icon}
                                                 alt="MetaMask"/>
                                            <div className={styles.walletType}><p>MetaMask</p>
                                                {isMetaMaskDetected && <div className={styles.detect}>detected</div>}
                                            </div>
                                        </div>

                                        {/* 3. OKX */}
                                        <div className={styles.connectOption}
                                             onClick={() => handleOKXEVM(selectedChain)}>
                                            <img className={styles.walletIcon}
                                                 src={isMobile ? okx_icon_mobile : okx_icon}
                                                 alt="OKX"/>
                                            <div className={styles.walletType}><p>OKX Wallet</p>
                                                {isOkxDetected && <div className={styles.detect}>detected</div>}
                                            </div>
                                        </div>

                                        {/* 4. Coinbase */}
                                        <div className={styles.connectOption}
                                             onClick={() => handleCoinbaseEVM(selectedChain)}>
                                            <img className={styles.walletIcon}
                                                 src={isMobile ? coinbaseWalletIconMobile : coinbaseWalletIconDesktop}
                                                 alt="Coinbase"/>
                                            <div className={styles.walletType}><p>Coinbase Wallet</p>
                                                {isCoinbaseDetected && <div className={styles.detect}>detected</div>}
                                            </div>
                                        </div>

                                    </>
                                )}

                                <div style={{background: "#1c1c1c"}}>
                                    {connectedPublicKey && !userEmail && (
                                        <div className={styles.lineWrapper}>
                                            <div className={styles.dividerLineFullLower}></div>
                                        </div>
                                    )}

                                    {connectedPublicKey && !userEmail && (
                                        <div
                                            className={`${styles.connectOption} ${styles.disconnectOptionOverride}`}
                                            onClick={disconnectWallet}
                                        >
                                            <img style={{width: "30px", height: "30px"}}
                                                 className={styles.walletIcon}
                                                 src={disconnectImg} alt="disconnect"/>
                                            <p style={{color: 'red', lineHeight: "30px"}}>Disconnect</p>
                                        </div>
                                    )}

                                    <div className={styles.lineWrapper}>
                                        <div className={styles.dividerLine}></div>
                                        <div className={styles.or}>Or</div>
                                        <div className={styles.dividerLine}></div>
                                    </div>

                                    {/* Email */}
                                    {userEmail ? (
                                        <div className={styles.connectOption}>
                                            <img
                                                style={{marginRight: '17px', width: '30px'}}
                                                src={email_svg}
                                                alt="email"
                                            />
                                            <div className={styles.emailType} onClick={handleLogoutWithEmail}>
                                                <p>{userEmail}</p>
                                                <img
                                                    style={{marginRight: '17px', width: '20px'}}
                                                    src={emailLogoutImg}
                                                    alt="email"
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        <div
                                            className={styles.connectOption}
                                            onClick={handleLoginWithEmail}
                                        >
                                            <img
                                                style={{marginRight: '17px', width: '30px'}}
                                                src={email_svg}
                                                alt="email"
                                            />
                                            <p>Connect with Email</p>
                                        </div>
                                    )}

                                </div>
                            </div>
                        </div>
                    )}
                </div>
            <ConnectModal
                isOpen={isConnectModalOpen}
                onClose={() => {
                    setIsConnectModalOpen(false);
                    setOpenWalletListOnConnectModalOpen(false);
                    setForceWalletConnectModalView(false);
                }}
                selectedChain={selectedChain}
                chainOptions={CHAIN_OPTIONS}
                onChainSelect={handleChainSelect}
                onWalletConnect={handleModalWalletConnect}
                userEmail={userEmail}
                connectedWalletAddress={connectedPublicKey}
                connectedWalletType={connectedWalletType}
                onEmailDisconnect={handleLogoutWithEmail}
                onWalletDisconnect={disconnectWallet}
                onWalletUnlink={handleModalWalletUnlink}
                showNotification={showNotification}
                openWalletListOnOpen={openWalletListOnConnectModalOpen}
                forceWalletConnectView={forceWalletConnectModalView}
                walletDetections={{
                    phantom: isPhantomDetected,
                    metamask: isMetaMaskDetected,
                    okx: isOkxDetected,
                    coinbase: isCoinbaseDetected
                }}
            />
        </div>
    );
};

export default ConnectWalletHeader;
